import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Droplets, Scissors, Calendar } from 'lucide-react';
import { BATH_SIZES, formatPrice } from '@/data/site';
import { usePlanModal } from './ContactModal';
import { SectionHeading } from './fx/SectionHeading';
import { Magnetic } from './fx/Magnetic';
import { Photo } from './fx/Photo';
import { burst } from './fx/Burst';
import { Wave } from './fx/Wave';
import { Paw, Sparkle, WhatsAppIcon, DoodleArrow } from './brand/Shapes';

const sizeForKg = (kg) => BATH_SIZES.find((s) => kg <= s.maxKg) || BATH_SIZES[BATH_SIZES.length - 1];
const DEFAULT_KG = { pequeno: 6, medio: 18, grande: 32 };

// cada porte com a sua cor pastel (pequeno menta, médio rosa, grande lilás)
const THEME = {
  pequeno: { tab: 'bg-pet-mint', ink: 'text-pet-mint-ink', fill: '#B9DD8E', paw: 'w-3.5 h-3.5', photoPos: '50% 55%' },
  medio: { tab: 'bg-pet-pink', ink: 'text-pet-pink-ink', fill: '#FF9FAC', paw: 'w-[18px] h-[18px]', photoPos: '50% 40%' },
  grande: { tab: 'bg-pet-lilac', ink: 'text-[#4B2F8F]', fill: '#B7A0EC', paw: 'w-[22px] h-[22px]', photoPos: '50% 30%' },
};

// ícone e ficha de cada serviço, só com cores da paleta
const ROW_STYLE = {
  banho: { Icon: Droplets, chip: 'bg-pet-sky text-pet-blue' },
  higienica: { Icon: Scissors, chip: 'bg-pet-pink-soft text-pet-pink-deep' },
  completa: { Icon: Scissors, chip: 'bg-pet-lilac/60 text-[#6B4FC0]' },
  pacote: { Icon: Calendar, chip: 'bg-pet-mint text-pet-mint-ink' },
};

/** Preço que "pula" ao trocar de porte, dentro de uma etiquetinha. */
const PriceTag = ({ value, selected }) => (
  <motion.span
    className={`relative inline-flex items-center rounded-[14px] pl-5 pr-3 py-1 font-display text-[1.6rem] md:text-[2.1rem] leading-none ${
      selected ? 'bg-pet-blue text-white' : 'bg-pet-blue-soft text-pet-blue'
    }`}
    animate={{ rotate: selected ? -4 : 0, scale: selected ? 1.06 : 1 }}
    transition={{ type: 'spring', stiffness: 500, damping: 14 }}
  >
    <span className={`absolute left-2 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full ${selected ? 'bg-white/80' : 'bg-pet-blue/40'}`} />
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.span
        key={value}
        initial={{ y: 20, scale: 0.4, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        exit={{ y: -20, scale: 0.4, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 500, damping: 16 }}
        className="inline-block tabular-nums"
      >
        {value}
      </motion.span>
    </AnimatePresence>
  </motion.span>
);

export const Pricing = () => {
  const { openPlan } = usePlanModal();
  const [kg, setKg] = useState(DEFAULT_KG.pequeno);
  const [rowId, setRowId] = useState('banho');
  const size = sizeForKg(kg);
  const theme = THEME[size.id];
  const rows = size.rows.filter((r) => r.price !== null);
  const row = rows.find((r) => r.id === rowId) || rows[0];

  return (
    <section id="banho" data-nav="banho" className="relative bg-pet-cream py-20 md:py-28">
      <Wave color="#FDFBF7" className="absolute bottom-[calc(100%-1px)] inset-x-0" />
      <div className="container-cp">
        <SectionHeading
          kicker="Serviços de banho"
          icon={Droplets}
          kickerClass="bg-pet-lilac/60 text-[#4B2F8F]"
          parts={[{ text: 'Banho' }, { text: '& Tosa', className: 'text-pet-blue sticker-text' }]}
          note="Deixe seu pet limpinho e cheiroso com nossos pacotes especiais. Escolha o porte e veja os valores 👇"
          className="mb-10 md:mb-14"
        />

        <div className="grid lg:grid-cols-12 gap-5 md:gap-6">
          {/* Foto do porte */}
          <div className="lg:col-span-5 relative rounded-[36px] md:rounded-[48px] overflow-hidden bg-pet-sky min-h-[280px] sm:min-h-[380px] lg:min-h-[640px] ring-[7px] ring-white shadow-[0_30px_60px_-30px_rgba(77,150,255,0.6)]">
            <AnimatePresence initial={false}>
              <motion.div
                key={size.id}
                className="absolute inset-0"
                initial={{ clipPath: 'circle(0% at 50% 60%)', scale: 1.2 }}
                animate={{ clipPath: 'circle(80% at 50% 60%)', scale: 1 }}
                exit={{ opacity: 0.99 }}
                transition={{ duration: 0.8, ease: [0.34, 1.3, 0.64, 1] }}
              >
                <Photo photo={size.photo} sizes="(min-width: 1024px) 40vw, 100vw" className="w-full h-full object-cover" style={{ objectPosition: theme.photoPos }} />
              </motion.div>
            </AnimatePresence>
            <AnimatePresence mode="wait">
              <motion.div
                key={size.id}
                className={`absolute left-4 top-4 lg:top-auto lg:bottom-6 lg:left-6 rounded-[22px] ${theme.tab} ${theme.ink} px-4 py-2 lg:px-5 lg:py-3 shadow-xl`}
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: -4 }}
                exit={{ scale: 0, rotate: 10 }}
                transition={{ type: 'spring', stiffness: 400, damping: 14 }}
              >
                <span className="flex items-center gap-2 font-display text-2xl lg:text-5xl leading-none">
                  {size.label} <Paw className="w-5 h-5 lg:w-8 lg:h-8 opacity-80" />
                </span>
                <span className="block mt-0.5 text-sm lg:text-base font-extrabold">{size.range}</span>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Seletor + preços */}
          <div className="lg:col-span-7 relative rounded-[36px] md:rounded-[48px] bg-white dots-bg p-5 sm:p-8 md:p-10 shadow-[0_30px_70px_-40px_rgba(45,55,72,0.35)] ring-1 ring-pet-blue/10">
            <span className="absolute -top-3 right-10 w-28 h-7 bg-pet-pink/80 rotate-6 rounded-sm shadow-sm" aria-hidden="true" />

            <div className="flex items-center justify-between gap-4">
              <p className="font-display text-2xl md:text-3xl text-pet-ink">Qual o porte do seu pet?</p>
            </div>

            <div role="tablist" aria-label="Porte do pet" className="mt-5 grid grid-cols-3 gap-2">
              {BATH_SIZES.map((s) => {
                const on = s.id === size.id;
                const t = THEME[s.id];
                return (
                  <motion.button
                    key={s.id}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={(e) => {
                      setKg(DEFAULT_KG[s.id]);
                      burst(e, 'paw', 6);
                    }}
                    whileTap={{ scale: 0.9 }}
                    animate={on ? { scaleY: [1, 0.85, 1.08, 1], y: -3 } : { scaleY: 1, y: 0 }}
                    transition={{ duration: 0.45 }}
                    className={`rounded-[22px] py-2.5 md:py-3 text-center border-2 transition-colors duration-300 ${
                      on ? `${t.tab} ${t.ink} border-white shadow-[0_8px_18px_-8px_rgba(45,55,72,0.4)]` : 'bg-pet-cream text-pet-ink/60 border-transparent hover:bg-white hover:border-pet-blue/15'
                    }`}
                  >
                    <span className="flex h-6 items-end justify-center mb-0.5" aria-hidden="true">
                      <Paw className={t.paw} />
                    </span>
                    <span className="block font-display text-[16px] md:text-lg leading-tight">{s.label}</span>
                    <span className="block text-[12px] md:text-[13px] font-extrabold opacity-80">{s.range}</span>
                  </motion.button>
                );
              })}
            </div>

            {/* régua de peso */}
            <div className="mt-6">
              <div className="flex items-center justify-between text-sm font-extrabold text-pet-ink/70 mb-2">
                <label htmlFor="peso">Ou arraste a patinha até o peso 🐾</label>
                <span className="font-display text-xl text-pet-blue tabular-nums">{kg >= 50 ? '50+' : kg} kg</span>
              </div>
              <div className="relative h-12 flex items-center">
                <div className="absolute inset-x-0 h-3 rounded-full bg-pet-cream overflow-hidden">
                  <motion.div className="h-full rounded-full" animate={{ backgroundColor: theme.fill }} style={{ width: `${((kg - 1) / 49) * 100}%` }} />
                </div>
                {[10, 25].map((mark) => (
                  <span key={mark} className="absolute top-full -mt-1 text-[11px] font-extrabold text-pet-ink/50 -translate-x-1/2" style={{ left: `${((mark - 1) / 49) * 100}%` }}>
                    {mark}kg
                  </span>
                ))}
                <input
                  id="peso"
                  type="range"
                  min={1}
                  max={50}
                  value={kg}
                  onChange={(e) => setKg(Number(e.target.value))}
                  aria-valuetext={`${kg} quilos — porte ${size.label}`}
                  className="absolute inset-0 w-full opacity-0 cursor-grab active:cursor-grabbing"
                />
                <motion.span
                  className="pointer-events-none absolute w-12 h-12 -ml-6 rounded-full text-white flex items-center justify-center shadow-lg ring-4 ring-white"
                  style={{ left: `${((kg - 1) / 49) * 100}%` }}
                  animate={{ rotate: (kg - 25) * 1.4, backgroundColor: theme.fill }}
                  transition={{ type: 'spring', stiffness: 300, damping: 12 }}
                >
                  <Paw className="w-6 h-6" />
                </motion.span>
              </div>
            </div>

            {/* tabela — sempre 4 linhas, para a altura não "pular" ao trocar de porte */}
            <ul className="mt-9 space-y-2" aria-live="polite">
              {size.rows.map((r) => {
                const available = r.price !== null;
                const selected = available && row.id === r.id;
                const { Icon, chip } = ROW_STYLE[r.id];
                return (
                  <li key={r.id}>
                    <motion.button
                      type="button"
                      disabled={!available}
                      onClick={() => setRowId(r.id)}
                      aria-pressed={selected}
                      whileTap={available ? { scale: 0.97 } : {}}
                      className={`w-full min-h-[70px] flex items-center gap-3 md:gap-4 rounded-[24px] border-2 px-3 md:px-4 py-3 md:py-3.5 text-left transition-colors duration-300 ${
                        selected
                          ? 'bg-pet-pink-soft border-dashed border-pet-pink-deep'
                          : available
                            ? 'bg-white/70 border-transparent hover:bg-white hover:border-pet-blue/15'
                            : 'bg-pet-cream/70 border-transparent cursor-default'
                      }`}
                    >
                      <motion.span
                        className={`shrink-0 w-11 h-11 md:w-12 md:h-12 rounded-2xl flex items-center justify-center ${available ? chip : 'bg-white text-pet-ink/30'}`}
                        animate={selected ? { rotate: -6, scale: [1, 1.2, 0.95, 1] } : { rotate: 0, scale: 1 }}
                        transition={{ duration: 0.45 }}
                      >
                        <Icon className="w-5 h-5" />
                      </motion.span>
                      <span className="flex-1 min-w-0">
                        <span className={`block text-[16px] md:text-lg font-extrabold ${available ? 'text-pet-ink' : 'text-pet-ink/40'}`}>{r.label}</span>
                        {available && r.tag && (
                          <span className="inline-flex items-center gap-1 text-xs font-extrabold text-[#6B4FC0]">
                            <Sparkle className="w-3 h-3" /> {r.tag}
                          </span>
                        )}
                        {available && r.note && <span className="block text-xs font-extrabold text-pet-mint-ink">{r.note}</span>}
                      </span>
                      {available ? (
                        <PriceTag value={formatPrice(r.price)} selected={selected} />
                      ) : (
                        <span className="shrink-0 rounded-full bg-pet-lilac/40 px-3 py-1.5 text-[12px] font-extrabold text-[#4B2F8F] text-center leading-tight">
                          só nos portes
                          <br />P e M
                        </span>
                      )}
                    </motion.button>
                  </li>
                );
              })}
            </ul>

            <div className="mt-7 flex flex-col md:flex-row md:items-center gap-3 md:gap-4 justify-between">
              <p className="font-hand text-2xl leading-tight text-pet-blue">
                você escolheu: {row.label}{' '}
                <span className="whitespace-nowrap">
                  (porte {size.label}) ✨
                  <DoodleArrow className="hidden md:inline-block align-middle ml-1 w-10 h-7 text-pet-pink-deep" />
                </span>
              </p>
              <Magnetic className="w-full md:w-auto shrink-0">
                <button
                  type="button"
                  onClick={(e) => {
                    burst(e, 'heart');
                    openPlan(`${row.label} — Porte ${size.label}`);
                  }}
                  className="btn-blue w-full sm:w-auto pl-2.5 whitespace-nowrap"
                >
                  <span className="w-9 h-9 rounded-full bg-pet-whats flex items-center justify-center">
                    <WhatsAppIcon className="w-5 h-5" />
                  </span>
                  Quero agendar
                </button>
              </Magnetic>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
